import { PropsWithChildren, useCallback, useEffect, useState } from 'react';
import { Flex, Box, ClickableBox, Button } from 'components/ui';
import { Row, Col } from 'react-grid-system';
import CollectionFilters from './CollectionFilters';
import CollectionItemsList from './CollectionItemsList';
import filtersIcon from 'img/filters.svg';
import { platformCollectionIds } from 'utils/config';
import { useWindowSize } from 'hooks/useWindowSize';
import { GRID_BREAKPOINTS, GalleryView } from '../constants';
import DeleteWhiteIcon from '../img/delete-white.svg';
import FuturaGalleryTokenIDFilter from './FuturaGalleryTokenIDFilter';
import { useQueryParam, ArrayParam, withDefault, StringParam } from 'use-query-params';

const MyFiltersParam = withDefault(ArrayParam, []);

const ActiveStateButton = ({
  active,
  onClick,
  children,
}: PropsWithChildren<{ active: boolean; onClick: any }>) => {
  return (
    <Button
      background={active ? 'black' : '#333333'}
      color="white"
      onClick={onClick}
      flexGrow={1}
      boxShadow="none"
      borderRadius={0}
      width="100%"
    >
      {children}
    </Button>
  );
};

const SelectAllOrOwnedButtons = ({
  onlyOwned,
  setOnlyOwned,
}: {
  setOnlyOwned(boolean): any;
  onlyOwned: boolean;
}) => {
  return (
    <Box display="flex" flexWrap="nowrap" width="100%" flexShrink={0}>
      <ActiveStateButton active={!onlyOwned} onClick={() => setOnlyOwned(false)}>
        All mixes
      </ActiveStateButton>
      <ActiveStateButton active={onlyOwned} onClick={() => setOnlyOwned(true)}>
        My mixes
      </ActiveStateButton>
    </Box>
  );
};

const ClearFiltersButton = ({
  disabled,
  clearFilters,
  checkedCount,
}: {
  disabled: boolean;
  clearFilters(): any;
  checkedCount: number;
}) => {
  const ws = useWindowSize();

  return (
    <Button
      borderRadius={1}
      border="1px solid #333"
      disabled={disabled}
      boxShadow={0}
      padding="5px 10px"
      margin={ws.width < GRID_BREAKPOINTS.xxl ? '10px' : '0 15px 0 0'}
      background="#111111"
      onClick={clearFilters}
    >
      <Flex width="100%" height="100%" justifyCenter={ws.width < GRID_BREAKPOINTS.xxl}>
        <img src={DeleteWhiteIcon} alt="Clear" width="16px" />
        <Box marginLeft={2}>Clear Filters</Box>
        {!disabled && checkedCount > 0 && (
          <Box
            width="35px"
            border="1px solid #333"
            marginLeft={2}
            borderRadius={15}
            background="black"
            color="white"
          >
            {checkedCount}
          </Box>
        )}
      </Flex>
    </Button>
  );
};

const FuturaGalleryView = ({ collectionStats }) => {
  const [checked, setChecked] = useQueryParam('filters', MyFiltersParam);
  const [galleryView, setGalleryView] = useQueryParam('view', StringParam);
  const setOnlyOwned = useCallback(
    (onlyOwned: boolean) => {
      if (onlyOwned) {
        setGalleryView(GalleryView.OWNED);
      } else {
        setGalleryView(GalleryView.ALL);
      }
    },
    [setGalleryView]
  );

  const ws = useWindowSize();
  const isXs = ws.width < GRID_BREAKPOINTS.sm;
  const [filtersVisible, setFiltersVisible] = useState(!isXs);

  useEffect(() => {
    setFiltersVisible(!isXs);
  }, [isXs]);

  const onAttributeChecked = (name: string, checked: string[]) => {
    const currentIndex = checked.indexOf(name);
    const newChecked = [...checked];
    if (currentIndex === -1) {
      newChecked.push(name);
    } else {
      newChecked.splice(currentIndex, 1);
    }
    setChecked(newChecked);
  };

  const clearFilters = () => setChecked([]);

  return (
    <Box width="100%">
      <Row style={{ padding: '0 15px' }}>
        <Col xs={12} sm={6} lg={4} xl={3}>
          {!isXs && (
            <>
              <Flex height={80} paddingX={2} column alignItems="start" justifyCenter>
                <Box fontSize="20px" fontWeight="bold">
                  Gallery
                </Box>
                <Box fontSize="12px">View EulerBeats releases from every drop to date</Box>
              </Flex>

              <Flex
                borderRadius="5px 5px 0 0"
                backgroundColor="black"
                flexDirection={ws.width < GRID_BREAKPOINTS.xxl ? 'column' : 'row'}
                alignItems={ws.width < GRID_BREAKPOINTS.xxl ? 'space-between' : 'center'}
                justifyContent={ws.width < GRID_BREAKPOINTS.xxl ? 'center' : 'space-between'}
                boxShadow="2px 5px 10px 0 black"
                flexGrow={1}
                borderBottom="1px solid #333"
                width="100%"
                wrap="true"
              >
                <SelectAllOrOwnedButtons
                  onlyOwned={galleryView === GalleryView.OWNED}
                  setOnlyOwned={setOnlyOwned}
                />
                <Flex
                  justifyContent={ws.width < GRID_BREAKPOINTS.xxl ? 'center' : ''}
                  fontWeight="bold"
                  fontSize="20px"
                  p={3}
                  alignCenter
                  flexBasis={ws.width < GRID_BREAKPOINTS.xxl ? 40 : 160}
                >
                  <img src={filtersIcon} alt="Clear" width="32px" />
                  <Box marginLeft={2}>Filters</Box>
                </Flex>

                <ClearFiltersButton
                  clearFilters={clearFilters}
                  disabled={!checked || checked.length === 0}
                  checkedCount={checked ? checked.length : 0}
                />
              </Flex>
            </>
          )}

          <Box
            width="100%"
            backgroundColor="black"
            borderRadius="0 0 5px 5px"
            border="2px solid black"
            boxShadow="2px 2px 10px 0 black"
            height={isXs ? 'auto' : 'calc(100vh - 340px)'}
            overflowY={isXs ? 'auto' : 'scroll'}
          >
            {isXs && (
              <Flex justifyCenter alignCenter padding={10} width="100%">
                <ClickableBox
                  width="100%"
                  display="flex"
                  justifyContent="center"
                  paddingY={2}
                  alignContent="center"
                  backgroundColor={filtersVisible ? 'black' : 'none'}
                  borderBottom={filtersVisible ? '1px solid #333' : ''}
                  onClick={() => setFiltersVisible(!filtersVisible)}
                >
                  <img src={filtersIcon} alt="Toggle filters" width="32px" />
                  <Flex marginLeft={2} alignCenter>
                    {filtersVisible ? 'Close' : 'Open'} Filters
                  </Flex>
                </ClickableBox>
              </Flex>
            )}

            {filtersVisible && (
              <>
                {isXs && (
                  <>
                    <SelectAllOrOwnedButtons
                      onlyOwned={galleryView === GalleryView.OWNED}
                      setOnlyOwned={setOnlyOwned}
                    />
                    <ClearFiltersButton
                      clearFilters={clearFilters}
                      disabled={!checked || checked.length === 0}
                      checkedCount={checked ? checked.length : 0}
                    />
                  </>
                )}
                <CollectionFilters
                  collectionStats={collectionStats}
                  checked={checked as string[]}
                  onAttributeChecked={onAttributeChecked}
                />
              </>
            )}
          </Box>
        </Col>

        <Col xs={12} sm={6} lg={8} xl={9}>
          <Flex alignCenter justifyContent="end" height="80px" marginX="15px">
            <FuturaGalleryTokenIDFilter />
          </Flex>

          <Box height="calc(100vh - 250px)" overflow="hidden">
            <CollectionItemsList
              collectionId={platformCollectionIds.futura}
              checked={checked as string[]}
              onlyOwned={galleryView === GalleryView.OWNED}
            />
          </Box>
        </Col>
      </Row>
    </Box>
  );
};

export default FuturaGalleryView;
